// ignore-string-externalization

import { contentfulGql } from '../../utils/contentfulGraphQL';
import { getCurrentUTCTimestamp } from '../../utils/dateUtils';
import { WEBGATE_DOMAIN } from '../../@mrkt/features/webgate-fetch';
import { BLOG_QUERY, HEROES_QUERY } from './blogQueries';
import type { BlogItem } from './blogQueries';
import type {
  GetBlogPostsQuery,
  GetBlogPostsQueryVariables,
  GetHeroesQuery,
  GetHeroesQueryVariables,
} from '../../graphql/generated';

export type FetchBlogParams = {
  offset?: number;
  limit?: number;
  smartlingLanguage?: string;
  preview?: boolean;
};

export async function fetchBlogData({
  offset = 0,
  limit = 10,
  smartlingLanguage = 'en-US',
  preview = false,
}: FetchBlogParams = {}) {
  const data = await contentfulGql<GetBlogPostsQuery>(
    BLOG_QUERY,
    {
      limit,
      skip: offset,
      locale: smartlingLanguage,
      now: getCurrentUTCTimestamp(),
    } satisfies GetBlogPostsQueryVariables,
    preview,
  );

  const collection = data.articlePageCollection;
  return {
    items: (collection?.items ?? []).filter(Boolean) as BlogItem[],
    total: collection?.total ?? 0,
  };
}

// Return shape preserved from Redux store (heros.ts) to avoid changes in consuming components
export async function fetchHeros() {
  const data = await contentfulGql<GetHeroesQuery>(HEROES_QUERY, {
    locale: 'en-US',
  } satisfies GetHeroesQueryVariables);

  const items = (data.headerImageCollection?.items ?? []).filter(Boolean);
  const artists = items.map(item => item!.artistId).join(',');

  let publicData: Record<string, any> = {};
  try {
    publicData = await fetch(
      `${WEBGATE_DOMAIN}/s4x-insights-api/v1/artist/public-data?artists=${artists}`,
    ).then(res => (res.ok ? res.json() : {}));
  } catch {
    /* ignore */
  }

  const heros = items.map(item => ({
    listeners: publicData[item!.artistId ?? ''],
    artistId: item!.artistId,
    artistName: item!.artistName,
    bannerImage: item!.bannerImage?.url,
  }));

  return {
    heros,
    heroIndex: Math.floor(Math.random() * heros.length),
  };
}
